import './ConsoleNavMenu.css';
import { useState, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import BatteryBox from './navMenu/BatteryBox';
import MsgBox from './navMenu/MsgBox';
import ModeBox from './navMenu/ModeBox';
import PageBar from '../layout/PageBar';
import IconBackUrl from '../../assets/icons/back.svg'
import useDroneHubConnection from '../../hooks/DroneHubConnection';
import { ModemData } from '../../api/Drone';
import {
    HubHbStatusMessage, HubHbStatusSchema,
    HubStatusTextMessage,
    HubTelemetryMessage, HubTelemetrySchema
} from '../../asp/DroneHub';
import { DroneFilterId } from '../../hooks/useDroneFilter';
import { BatteryRemainingData } from '../../types/Battery';

interface ConsoleNavMenuProps {
    drone?: ModemData | null;
    unselectModem?: () => void;
    toggleShowMenu?: () => void;
    startMsgs?: HubStatusTextMessage[];
    /** Счётчики парка для сводки, когда дрон ещё не выбран. */
    counts?: Record<DroneFilterId, number>;
    /** Режим просмотра консоли и его переключатель. */
    view?: 'map' | 'grid';
    onViewChange?: (view: 'map' | 'grid') => void;
}

/**
 * Второй этаж шапки в консоли: телеметрия выбранного дрона, а до выбора —
 * сводка по парку. Логин, навигация и меню пользователя переехали в AppHeader,
 * здесь остаётся только то, что меняется вместе с дроном.
 */
function ConsoleNavMenu({ drone = null, toggleShowMenu, unselectModem, startMsgs, counts, view, onViewChange }: ConsoleNavMenuProps): ReactNode {
    const droneHub = useDroneHubConnection();
    const { t } = useTranslation(['console']);

    const [updateTelemFromHub, setUpdateTelemFromHub] = useState<HubTelemetryMessage | null>(null);
    const [hbUpdate, setHbUpdate] = useState<HubHbStatusMessage | null>(null);
    const [telemetry, setTelemetry] = useState<HubTelemetryMessage[]>([]);
    const [currentTelemetry, setCurrentTelemetry] = useState<HubTelemetryMessage | null>(null);

    const [imei, setImei] = useState<string>(drone?.imei ?? "");
    const [arm, setArm] = useState<boolean>(drone?.isArm ?? false);
    const [uavMode, setUavMode] = useState<number>(drone?.mode ?? -1);
    const [uavType, setUavType] = useState<number>(drone?.mavType ?? -1);
    const [mName, setMName] = useState<string>(drone?.name ?? "");
    const [isOnline, setIsOnline] = useState<boolean>(drone?.isOnline ?? false);

    const [_batteryRemaining, setBatteryRemaining] = useState<BatteryRemainingData[]>(drone?.batteryRemaining ?? []);

    // Запрос /user/start отсюда убран: логин и права нужны шапке, и она же их
    // теперь запрашивает — один раз на приложение вместо двух независимых.

    useEffect(() => {
        const { connection } = droneHub;

        // Сообщения хаба проверяем схемой, как это делает NavigationData.
        // Раньше сырое сообщение клалось в state как есть, и batteryRemaining
        // не-массивом ронял BatteryBox, а с ним и всё дерево React: у
        // приложения нет error boundary, поэтому экран просто чернел.
        const onHbStatus = (message: HubHbStatusMessage): void => {
            const result = HubHbStatusSchema.safeParse(message);
            if (result.success) {
                setHbUpdate(result.data);
            } else {
                logger.error('HbStatus: неожиданный формат', result.error, message);
            }
        };

        const onTelemetry = (message: HubTelemetryMessage): void => {
            const result = HubTelemetrySchema.safeParse(message);
            if (result.success) {
                setUpdateTelemFromHub(result.data);
            } else {
                // Показываем последнюю удачную телеметрию вместо падения.
                logger.error('Telemetry: неожиданный формат', result.error, message);
            }
        };

        connection.on('HbStatus', onHbStatus);
        connection.on('Telemetry', onTelemetry);

        return () => {
            connection.off('HbStatus', onHbStatus);
            connection.off('Telemetry', onTelemetry);
        };
    }, [droneHub]);

    useEffect(() => {
        if (hbUpdate != null) {
            if (hbUpdate.imei === drone?.imei) {
                setArm(hbUpdate.isArm);
                setUavMode(hbUpdate.mode);
                setUavType(hbUpdate.type);
            }
        }
    }, [hbUpdate]);

    useEffect(() => {
        //logger.debug("update drone dd");
        if (drone != null) {
            setArm(drone.isArm);
            setUavMode(drone.mode);
            setImei(drone.imei);
            setBatteryRemaining(drone.batteryRemaining ?? []);
            setUavType(drone?.mavType);
        }
        //logger.debug(drone);
        setIsOnline(drone?.isOnline ?? false);
        setMName(drone?.name ?? "");
        switchTelemetry();
        //todo: request api
    }, [drone]);

    useEffect(() => {
        if (updateTelemFromHub) {
            setTelem(updateTelemFromHub);
        }
    }, [updateTelemFromHub]);

    useEffect(() => {
        logger.debug("toggleShowMenu rerender!");
    }, [toggleShowMenu]);

    function switchTelemetry() {
        if (drone === null) {
            return;
        }
        setCurrentTelemetry(telemetry.find((item) => item.imei === drone.imei) ?? null);
    }

    function setTelem(msg: HubTelemetryMessage): void {
        setTelemetry((telems) => {
            const itemExists = telems.some((item) => item.imei === msg.imei);
            if (itemExists) {
                return telems.map((t) =>
                    t.imei === msg.imei
                        //? { ...t, batteryRemaining: msg.batteryRemaining, height: msg.height, speed: msg.speed, sputniksCount: msg.sputniksCount }
                        ? { ...t, ...msg }
                        : t
                )
            } else {
                //return [...telems, {imei: msg.imei, batteryRemaining: msg.batteryRemaining, height: msg.height, speed: msg.speed, sputniksCount: msg.sputniksCount}];
                return [...telems, { ...msg }];
            }
        });

        if (msg?.imei === drone?.imei) {
            setCurrentTelemetry(msg);
        }
    }

    return (
        <PageBar>
            <div className='console-nav-menu'>
                {!mName ? (
                    /* Дрон не выбран — показываем сводку по парку. Числа те же,
                       что в чипсах DroneList: считает их один useDroneFilter.
                       Кнопка «показать все» живёт на самой карте. */
                    <div className='fleet-summary'>
                        <div className='fleet-summary-counts'>
                            <span className='fleet-summary-item'>
                                {t('fleet_total')}: <b>{counts?.all ?? 0}</b>
                            </span>
                            <span className='fleet-summary-item fleet-summary-online'>
                                {t('fleet_online')}: <b>{counts?.online ?? 0}</b>
                            </span>
                            <span className='fleet-summary-item'>
                                {t('fleet_arm')}: <b>{counts?.arm ?? 0}</b>
                            </span>
                            {(counts?.update ?? 0) > 0 &&
                                <span className='fleet-summary-item fleet-summary-update'>
                                    {t('fleet_update')}: <b>{counts?.update}</b>
                                </span>
                            }
                        </div>
                        {onViewChange &&
                            <div className='console-view-switch' role='group'>
                                <button
                                    type='button'
                                    className={'console-view-btn' + (view === 'map' ? ' console-view-btn-on' : '')}
                                    aria-pressed={view === 'map'}
                                    onClick={() => onViewChange('map')}
                                >
                                    {t('view_map')}
                                </button>
                                <button
                                    type='button'
                                    className={'console-view-btn' + (view === 'grid' ? ' console-view-btn-on' : '')}
                                    aria-pressed={view === 'grid'}
                                    onClick={() => onViewChange('grid')}
                                >
                                    {t('view_grid')}
                                </button>
                            </div>
                        }
                    </div>
                ) : (
                    <div className='telemetry-layout'>
                        <button
                            type='button'
                            className='header-box icon-container mobile-back-btn'
                            aria-label={t('back_to_list')}
                            onClick={() => { unselectModem?.(); }}
                        >
                            <img src={IconBackUrl} alt="" />
                        </button>

                        <div className="header-box header-left-box">
                            <div className='name-box'>{mName}</div>
                            <div className='arm-box'>{arm ? 'ARM' : 'DISARM'}</div>
                        </div>

                        <MsgBox imei={imei} startMsgs={startMsgs ?? []} />

                        <div className="header-box header-second-row-group">
                            <ModeBox type={uavType} mode={uavMode} isOnline={isOnline} />
                            <div className='sat-box'>
                                <img className='sat-box-icon' src="/image/modem/sat.svg" alt="" />
                                <div>{currentTelemetry?.sputniksCount ?? "—"}</div>
                            </div>
                            <BatteryBox batteries={currentTelemetry?.batteryRemaining ?? null} />
                        </div>

                        <div className='header-box menu-box' >
                            <button
                                type='button'
                                className='menu-box-sep'
                                aria-label={t('modem_settings')}
                                onClick={toggleShowMenu}
                            >
                                <img src="/image/modem/settings.svg" alt="" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </PageBar>
    )
}

export default ConsoleNavMenu;